import { forwardRef } from 'react';
import { cn } from '../../utils/cn';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  variant?: 'default' | 'glass' | 'minimal';
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      label,
      error,
      helperText,
      variant = 'glass',
      resize = 'none',
      rows = 3,
      ...props
    },
    ref
  ) => {
    const baseStyles =
      'w-full bg-transparent outline-none transition-all placeholder:text-slate-500 text-sm';

    const variants = {
      default:
        'bg-black/30 border border-white/5 rounded-xl p-4 text-white focus:ring-1 focus:ring-blue-600/50 focus:border-blue-600/50',
      glass:
        'bg-black/40 border border-white/5 rounded-2xl p-4 text-slate-300 focus:bg-black/60 focus:border-white/10 italic leading-relaxed',
      minimal:
        'border-b border-white/10 py-2 text-white focus:border-blue-500 px-0',
    };

    const resizeStyles = {
      none: 'resize-none',
      vertical: 'resize-y',
      horizontal: 'resize-x',
      both: 'resize',
    };

    const textareaStyles = cn(
      baseStyles,
      variants[variant],
      resizeStyles[resize],
      error && 'border-red-500/50 focus:border-red-500 focus:ring-red-500/50',
      className
    );

    return (
      <div className="w-full">
        {label && (
          <label className="block text-[11px] font-bold uppercase tracking-widest text-blue-500 mb-1.5">
            {label}
          </label>
        )}
        <textarea ref={ref} rows={rows} className={textareaStyles} {...props} />
        {error && (
          <p className="mt-1.5 text-[10px] text-red-400 font-medium">{error}</p>
        )}
        {helperText && !error && (
          <p className="mt-1.5 text-[10px] text-slate-500">{helperText}</p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export { Textarea };
